import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { AnalyticEvents } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { ApplicantInterviewers } from '@app/models/recruiting/applicant-interviewers.model';
import { ApplicantInterviews } from '@app/models/recruiting/applicant-interviews.model';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { InterviewQuestion } from '@app/models/recruiting/interview-question.model';
import { ApplicantInterviewQuestionTypes } from '@app/modules/applicant-tracker/enums/applicant-interview-question-types.enum';
import {
    CheckboxFormGroup,
    requireCheckboxesToBeCheckedValidator,
} from '@app/modules/applicant-tracker/functions/require-checkbox-validator.function';
import { Attach, Detach } from '@app/modules/applicant-tracker/interfaces/interviews-assign.interface';
import { InterviewService } from '@app/modules/applicant-tracker/services';
import { ApplicantInterviewersSummaryTable } from '@app/modules/applicant-tracker/services/tables/applicant-interviewers-summary-table/applicant-interviewers-summary-table';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import moment from 'moment';

type Interviewer = {
    employee: Employee;
    isSelected: boolean;
};

interface InterviewFormGroup {
    myCheckboxGroup: CheckboxFormGroup;
    feedbackDueBy: FormControl<moment.Moment | null>;
}

@Component({
    selector: 'app-job-posting-applicant-interviews-show',
    templateUrl: './job-posting-applicant-interviews-show.template.html',
    styleUrls: ['./job-posting-applicant-interviews-show.style.scss'],
})
export class JobPostingApplicantInterviewsShowView implements OnInit {
    applicantInterview: ApplicantInterviews;
    applicantInterviewersSummaryTable: ApplicantInterviewersSummaryTable;
    interviewQuestionsWithFeedbackResults: InterviewQuestion[] = [];
    applicantInterviewQuestionTypes = ApplicantInterviewQuestionTypes;
    formGroup: FormGroup<InterviewFormGroup>;
    interviewers: Interviewer[] = [];
    assignedApplicantInterviewers: ApplicantInterviewers[] = [];
    minDate = moment().format('YYYY-MM-DD HH:mm:ss');

    isAssignModalOpen = false;
    isDeleteModalOpen = false;
    hasRecruiterAccess = false;
    isLoading = false;
    isSaving = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private interviewService: InterviewService,
        private location: Location,
        private auth: AuthService,
        private analyticService: AnalyticService
    ) {
        this.applicantInterview = this.route.snapshot.data.models.get('applicantInterview');

        this.applicantInterviewersSummaryTable = new ApplicantInterviewersSummaryTable(
            this.auth,
            this.applicantInterview
        );

        this.hasRecruiterAccess = this.auth.can(Platform.recruiting.permission.recruiter);
    }

    get feedbackDueBy(): FormControl {
        return this.formGroup.controls.feedbackDueBy;
    }

    async ngOnInit(): Promise<void> {
        await this.fetchAssignedInterviewers();
        await this.getApplicantInterviewFeedbackResults();

        this.createFormGroup();
        this.setHiringTeamMembers();
    }

    async fetchAssignedInterviewers(): Promise<void> {
        this.isLoading = true;

        try {
            const [applicantInterviewers] = await ApplicantInterviewers.where(
                'atsApplicantInterviewId',
                this.applicantInterview.id
            )
                .with(['interviewer', 'applicantInterviewerFeedbacks'])
                .get();

            this.assignedApplicantInterviewers = applicantInterviewers;
            this.isLoading = false;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        }
    }

    async onSave(form: FormGroup): Promise<void> {
        if (!form.valid || this.isSaving) {
            return;
        }

        // This was moved here to prevent users from clicking save multiple times because they thought nothing was happening.
        // It's still possible to hit save multiple times, but less likely as the modal doesn't linger open for a very long time.
        this.isSaving = true;
        this.isAssignModalOpen = false;

        const assignedEmployees: Employee[] = this.assignedApplicantInterviewers.map(
            (applicantInterviewer: ApplicantInterviewers) => {
                return applicantInterviewer.interviewer;
            }
        );

        const attach: Attach = {
            interviewers: this.interviewers
                // Filter all assigned employees out of the interviewers list before attaching and detaching
                .filter(
                    (interviewer: Interviewer) =>
                        !assignedEmployees.some(
                            (assignedEmployee: Employee) => assignedEmployee.id === interviewer.employee.id
                        )
                )
                .filter((interviewer: Interviewer) => interviewer.isSelected)
                .map((interviewer: Interviewer) => interviewer.employee.id),
        };

        const detach: Detach = {
            interviewers: this.interviewers
                .filter((interviewer: Interviewer) => !interviewer.isSelected)
                // Check if interviewer is not in the assinged list if they aren't then don't add them to the detach array.
                .filter((interviewer: Interviewer) =>
                    assignedEmployees.some(
                        (assignedEmployee: Employee) => assignedEmployee.id === interviewer.employee.id
                    )
                )
                .map((interviewer: Interviewer) => interviewer.employee.id),
        };

        try {
            // If we have no new attach or detach items and the date is changed we need to update the using the applicantInterview endpoint instead.
            if (attach.interviewers.length || detach.interviewers.length) {
                await this.interviewService.assignApplicantToInterview(
                    this.applicantInterview.interview.id,
                    this.feedbackDueBy.value,
                    this.applicantInterview.applicant.id,
                    attach,
                    detach
                );

                this.analyticService.trackEvent(
                    AnalyticEvents.RecruitingJobPostingInterviewsApplicantInterviewEditInterviewInstanceClickSave
                );
                this.location.back();
            } else {
                this.applicantInterview.feedbackDueDate = this.feedbackDueBy.value;

                await this.applicantInterview.save();
            }
        } catch (err) {
            this.notify.error(err.error?.errors[0]?.detail || 'applicant-tracker.cannotAssignInterview');
        }

        this.isSaving = false;
    }

    async onDelete(): Promise<void> {
        try {
            await this.applicantInterview.delete();
            this.analyticService.trackEvent(
                AnalyticEvents.RecruitingJobPostingInterviewsApplicantInterviewDeleteInterviewInstance
            );
            this.location.back();
        } catch (err) {
            this.notify.error(err);
        }
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        const applicantInterviewer = data as ApplicantInterviewers;

        if (menuItem.event === ApplicantInterviewersSummaryTable.openOption.event) {
            this.analyticService.trackEvent(
                AnalyticEvents.RecruitingJobPostingInterviewsApplicantInterviewClickOpenToAccess
            );
            this.router.navigate(['/recruiting/interviews/manage', applicantInterviewer.applicantInterview.id]);
        }
    }

    goToApplicantPage(applicant: Applicant): void {
        this.router.navigate(['/recruiting', 'applicants', applicant.id]);
    }

    getApplicantInterviewHeader(): string {
        const applicantName = this.pluralize(this.applicantInterview.applicant?.fullName);

        return `${applicantName} ${this.applicantInterview.interview?.name}`;
    }

    onCancelAssigningInteriewers(): void {
        this.isAssignModalOpen = false;
    }

    openAssignModal(): void {
        this.isAssignModalOpen = true;
    }

    closeDeleteModal(): void {
        this.isDeleteModalOpen = false;
    }

    openDeleteModal(): void {
        this.isDeleteModalOpen = true;
    }

    private async getApplicantInterviewFeedbackResults(): Promise<void> {
        const interviewQuestionsWithFeedbackResults: InterviewQuestion[] =
            await this.interviewService.getApplicantInterviewFeedbackResults(this.applicantInterview);

        this.interviewQuestionsWithFeedbackResults = interviewQuestionsWithFeedbackResults;
    }

    private pluralize(value: string): string {
        const ending = value[value.length - 1] === 's' ? "'" : "'s";
        return value + ending;
    }

    private setHiringTeamMembers(): void {
        const assignedEmployees = this.assignedApplicantInterviewers.map(
            (applicantInterviewer: ApplicantInterviewers) => {
                return applicantInterviewer.interviewer;
            }
        );

        this.interviewers = this.applicantInterview.applicant.jobPosting.hiringTeam.map((employee: Employee) => {
            return {
                employee,
                isSelected: assignedEmployees.some((assignedEmployee: Employee) => {
                    return assignedEmployee.id === employee.id;
                }),
            };
        });
    }

    private createFormGroup(): void {
        const group = {};

        const assignedEmployees = this.assignedApplicantInterviewers.map(
            (applicantInterviewer: ApplicantInterviewers) => {
                return applicantInterviewer.interviewer;
            }
        );

        // Fill check if record exists
        this.applicantInterview.applicant.jobPosting.hiringTeam.forEach((employee: Employee) => {
            group[employee.id] = new FormControl({
                value: assignedEmployees.some((assignedEmployee: Employee) => {
                    return assignedEmployee.id === employee.id;
                }),
                // Disable if interviewer has submitted interview
                disabled: this.assignedApplicantInterviewers.find(
                    (assignedApplicantInterviewer: ApplicantInterviewers) =>
                        employee.id === assignedApplicantInterviewer.interviewer.id
                )?.isSubmitted,
            });
        });

        this.formGroup = new FormGroup<InterviewFormGroup>({
            myCheckboxGroup: new FormGroup({ ...group }, requireCheckboxesToBeCheckedValidator()),
            feedbackDueBy: new FormControl(moment(), [Validators.required]),
        });
    }
}
